import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import type { Account } from '@shared/products-types';
import { ImageUpload } from '@shared/core-ui-image-upload';
import { georgeStats, trackEvent2 } from '@shared/statistics';
import type { ProductWithHelpers } from '@shared/products-core';
import { useActiveProduct } from '@shared/products-core';
import { CustomizationService } from '@api-client/products';
import { dataURLtoBlob } from '@shared/image';

import { gAPIAccountEntitySlice } from '../gAPIAccountEntitySlice';
import { removeImageFromCache, saveImageToCache } from '../services/imageLoadingService';
import { invalidateProductImage } from '../papi-migration/hooks/useProductImage';

type Props = {
    dropdownToggle?: ReactNode;
    imageChangeContext: 'accountDetails' | 'overview';
    productType?: string;
} & Parameters<typeof trackEvent2<'productCustomisationChanges', 'productImageChangeStarted'>>[2];

const IMAGE_PREFIX_LENGTH = 22;

export const ProductImageUpload = forwardRef<HTMLInputElement, Props>(
    ({ dropdownToggle, imageChangeContext, trigger, productType = '' }, ref) => {
        const product = useActiveProduct<ProductWithHelpers<Account>>();
        const { id, hasImage, productI18N } = product;
        const handleImageUpload = (image: string) =>
            CustomizationService.postPublicProductsCustomizationImage({
                id,
                formData: { file: dataURLtoBlob(image) },
            }).then(() => {
                saveImageToCache(id, image.substr(IMAGE_PREFIX_LENGTH));
                gAPIAccountEntitySlice.updateCache({
                    ...(gAPIAccountEntitySlice.selectById(id) as Account),
                    hasImage: true,
                });
            });

        const handleImageCustomizationSuccess = () =>
            trackEvent2('productCustomisationChanges', 'productCustomised', {
                productType,
                customisationChanges: ['image'],
            });

        const handleImageCustomizationStart = () =>
            trackEvent2('productCustomisationChanges', 'productImageChangeStarted', {
                trigger,
            });

        const handleUploadedImageChange = () => {
            georgeStats.trackEvent(imageChangeContext, 'accountAvatarChanged', { productI18N });
            handleImageCustomizationSuccess();
        };

        const handleImageRemove = async () => {
            handleImageCustomizationStart();
            return CustomizationService.deletePublicProductsCustomizationImage({ id }).then(() => {
                invalidateProductImage(id);
                gAPIAccountEntitySlice.updateCache({
                    ...(gAPIAccountEntitySlice.selectById(id) as Account),
                    hasImage: false,
                });
                removeImageFromCache(id);
                handleImageCustomizationSuccess();
            });
        };

        return (
            <ImageUpload
                ref={ref}
                onUploadedImageChange={handleUploadedImageChange}
                onImageUpload={handleImageUpload}
                onImageRemove={handleImageRemove}
                disableRemoveImage={!hasImage}
                showResultAsNotification
                dropdownToggle={dropdownToggle}
                onChangeImageClick={handleImageCustomizationStart}
            />
        );
    },
);
